import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, pickBy } from 'lodash';
import { useParams } from 'react-router-dom';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FormDialog } from 'components';
import { FundInvestmentsParams } from 'pages/FundInvestments';
import {
  FUND_INVESTMENTS_DEFAULT_CURRENCY,
  FUND_INVESTMENTS_FORM_ADD_TITLE,
  FUND_INVESTMENTS_FORM_DELETE_TITLE,
  FUND_INVESTMENTS_FORM_DELETE_TOOLTIP,
} from 'pages/FundInvestments/common/constants';
import { useFundInvestment, useFundInvestmentForm } from 'pages/FundInvestments/hooks';
import { FundInvestmentFormSchema } from 'pages/FundInvestments/hooks/useFundInvestmentForm';
import { useDialogFormsStore } from 'store';
import { getBooleanValue, getObjectValue, getStringValue } from 'utilities';
import { FundInvestmentFormDialogProps } from './FundInvestmentFormDialog.props';

const FundInvestmentFormDialog: FC<FundInvestmentFormDialogProps> = props => {
  const { handleOnClose, open } = props;

  const [allowDeleteFundInvestment, setAllowDeleteFundInvestment] = useState(false);

  // Dialog forms store
  const shouldUpdateFundInvestment = useDialogFormsStore(state => state.shouldUpdateFundInvestment);

  const [storeValue] = useStore() as UseStoreValues;
  const { companyInfo: currentFundInvestment } = getObjectValue(storeValue);
  const { measurement_date_id: currentMeasurementDate } = getObjectValue(currentFundInvestment);

  const { firmSlugParam = '' } = useParams<FundInvestmentsParams>();

  const { confirmDeleteFundInvestment, handleCreateFundInvestment, handleUpdateFundInvestment } = useFundInvestment({
    firmSlugParam,
  });

  const activeFundInvestment = useMemo<FundInvestmentFormSchema>(
    () =>
      ({
        name: getStringValue(currentFundInvestment?.name),
        legal_company_name: getStringValue(currentFundInvestment?.legal_company_name),
        website: getStringValue(currentFundInvestment?.website),
        fiscal_year_end: getObjectValue(currentFundInvestment?.fiscal_year_end),
        captable_currency:
          getStringValue(currentFundInvestment?.captable_currency) ?? FUND_INVESTMENTS_DEFAULT_CURRENCY,
        vintage_year: currentFundInvestment?.vintage_year ?? null,
      }) as FundInvestmentFormSchema,
    [currentFundInvestment]
  );

  const { formState, FundInvestmentForm } = useFundInvestmentForm({
    fundInvestment: shouldUpdateFundInvestment ? activeFundInvestment : undefined,
  });

  const fundInvestmentIsEmpty = useMemo<boolean>(() => isEmpty(pickBy(formState.values)), [formState]);

  const handleOnSave = useCallback(
    () =>
      shouldUpdateFundInvestment
        ? handleUpdateFundInvestment(formState.values)
        : handleCreateFundInvestment(formState.values),
    [formState, handleCreateFundInvestment, handleUpdateFundInvestment, shouldUpdateFundInvestment]
  );

  // Allow delete the fund investment if it is in edit mode and does not have a measurement date
  useEffect(() => {
    if (shouldUpdateFundInvestment && !isEmpty(currentFundInvestment) && !currentMeasurementDate) {
      setAllowDeleteFundInvestment(true);
    }
  }, [currentFundInvestment, currentMeasurementDate, shouldUpdateFundInvestment]);

  return (
    <FormDialog
      allowDelete={allowDeleteFundInvestment}
      confirmCancel={!fundInvestmentIsEmpty}
      deleteText={FUND_INVESTMENTS_FORM_DELETE_TITLE}
      editMode={shouldUpdateFundInvestment}
      isValid={getBooleanValue(formState.isValid)}
      onClose={handleOnClose}
      onDelete={confirmDeleteFundInvestment}
      onSave={handleOnSave}
      open={open}
      shouldCheckFeatureEnabler={allowDeleteFundInvestment}
      title={shouldUpdateFundInvestment ? getStringValue(currentFundInvestment?.name) : FUND_INVESTMENTS_FORM_ADD_TITLE}
      toolTipText={FUND_INVESTMENTS_FORM_DELETE_TOOLTIP}>
      {FundInvestmentForm}
    </FormDialog>
  );
};

export default FundInvestmentFormDialog;
